import React, { type FormEvent, useCallback, useMemo, useState } from 'react'
import { Form } from 'react-bootstrap'
import { Button } from '../../../components/Button/Button'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { type OAuth2App, useUpdateOAuth2AppMutation } from '../../../store/auth/auth'
import { addToast } from '../../../store/toast/toastSlice'
import { type AppDetailsFormData, AppDetailsFormFields } from '../components/AppDetailsFormFields'
import { SHORT_DESCRIPTION_MAX } from '../constants'
import { RequestChangeModal } from './RequestChangeModal'

interface ManageAppDetailsTabProps {
  oAuth2App: OAuth2App
}

export const ManageAppDetailsTab: React.FC<ManageAppDetailsTabProps> = ({ oAuth2App }) => {
  const isExternal = oAuth2App.type === 'external'

  if (isExternal) {
    return <ExternalAppDetailsView oAuth2App={oAuth2App} />
  }

  return <EditableAppDetailsView oAuth2App={oAuth2App} />
}

const ExternalAppDetailsView: React.FC<ManageAppDetailsTabProps> = ({ oAuth2App }) => {
  const [showModal, setShowModal] = useState<boolean>(false)

  const formData: AppDetailsFormData = useMemo(() => {
    const about = oAuth2App.description?.about
    const whatYouCanDo = oAuth2App.description?.do

    return {
      name: oAuth2App.name,
      imageUrl: oAuth2App.imageUrl ?? '',
      shortDescription: oAuth2App.shortDescription ?? '',
      about: Array.isArray(about) ? about.join('\n') : typeof about === 'string' ? about : '',
      whatYouCanDo: Array.isArray(whatYouCanDo) ? whatYouCanDo.join('\n') : typeof whatYouCanDo === 'string' ? whatYouCanDo : '',
    }
  }, [oAuth2App.name, oAuth2App.imageUrl, oAuth2App.shortDescription, oAuth2App.description])

  return (
    <div>
      <h5 className='fw-bold mb-4'>App details</h5>
      <AppDetailsFormFields formData={formData} readOnly />
      <div className='d-flex justify-content-end'>
        <Button variant='primary' onClick={() => setShowModal(true)}>
          Request edit
        </Button>
      </div>
      <RequestChangeModal show={showModal} subject='details' onHide={() => setShowModal(false)} />
    </div>
  )
}

const EditableAppDetailsView: React.FC<ManageAppDetailsTabProps> = ({ oAuth2App }) => {
  const accountId = useAppSelector(selectAccountId)
  const dispatch = useAppDispatch()
  const [updateApp, { isLoading: isSaving }] = useUpdateOAuth2AppMutation()

  const about = oAuth2App.description?.about
  const whatYouCanDo = oAuth2App.description?.do
  const aboutText = Array.isArray(about) ? about.join('\n') : typeof about === 'string' ? about : ''
  const whatYouCanDoText = Array.isArray(whatYouCanDo) ? whatYouCanDo.join('\n') : typeof whatYouCanDo === 'string' ? whatYouCanDo : ''

  const [formData, setFormData] = useState<AppDetailsFormData>({
    name: oAuth2App.name,
    imageUrl: oAuth2App.imageUrl ?? '',
    shortDescription: oAuth2App.shortDescription ?? '',
    about: aboutText,
    whatYouCanDo: whatYouCanDoText,
  })
  const [validated, setValidated] = useState<boolean>(false)
  const [saveSuccess, setSaveSuccess] = useState<boolean>(false)

  const hasChanges =
    formData.name !== oAuth2App.name ||
    formData.imageUrl !== (oAuth2App.imageUrl ?? '') ||
    formData.shortDescription !== (oAuth2App.shortDescription ?? '') ||
    formData.about !== aboutText ||
    formData.whatYouCanDo !== whatYouCanDoText

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      setValidated(true)
      setSaveSuccess(false)

      if (!formData.name.trim() || !formData.shortDescription.trim()) {
        return
      }

      if (formData.shortDescription.length > SHORT_DESCRIPTION_MAX) {
        return
      }

      const aboutLines = formData.about
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line !== '')
      const whatYouCanDoLines = formData.whatYouCanDo
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line !== '')

      try {
        await updateApp({
          accountId,
          clientId: oAuth2App.clientId,
          payload: {
            name: formData.name.trim(),
            imageUrl: formData.imageUrl.trim() || null,
            shortDescription: formData.shortDescription.trim(),
            description: {
              ...oAuth2App.description,
              about: aboutLines,
              do: whatYouCanDoLines,
            },
          },
        }).unwrap()

        setSaveSuccess(true)
      } catch {
        dispatch(addToast({ variant: 'error', title: 'Error', messages: ['Failed to save changes. Please try again.'] }))
      }
    },
    [accountId, formData, oAuth2App.clientId, oAuth2App.description, updateApp, dispatch],
  )

  const handleDiscard = useCallback(() => {
    setFormData({
      name: oAuth2App.name,
      imageUrl: oAuth2App.imageUrl ?? '',
      shortDescription: oAuth2App.shortDescription ?? '',
      about: aboutText,
      whatYouCanDo: whatYouCanDoText,
    })
    setValidated(false)
    setSaveSuccess(false)
  }, [oAuth2App, aboutText, whatYouCanDoText])

  return (
    <Form noValidate validated={validated} onSubmit={handleSubmit}>
      <h5 className='fw-bold mb-4'>App details</h5>

      <AppDetailsFormFields formData={formData} setFormData={setFormData} validated={validated} />

      {saveSuccess ? <div className='text-success small mb-3'>Changes saved successfully.</div> : null}

      <div className='d-flex justify-content-end gap-3'>
        <Button variant='secondary' className='fw-bold' onClick={handleDiscard} disabled={!hasChanges || isSaving}>
          Discard
        </Button>
        <Button type='submit' variant='primary' className='fw-bold' disabled={!hasChanges || isSaving} loading={isSaving}>
          Save changes
        </Button>
      </div>
    </Form>
  )
}
